import * as React from 'react';
import useAmplifyTokenRefresh from './useAmplifyTokenRefresh';
import useGeneratedLiveToken from './useGeneratedLiveToken';
import useRequiredLivePageData from './useRequiredLivePageData';

export default function useAmplifyContextStartup() {
  const amplifyTokenRefresh = useAmplifyTokenRefresh();
  const generatedLiveToken = useGeneratedLiveToken(amplifyTokenRefresh.tokens);
  const cognitoOrg = amplifyTokenRefresh.tokens?.idToken.payload?.['custom:organisationId'] as string | undefined;
  const requiredLivePageData = useRequiredLivePageData(generatedLiveToken.token, cognitoOrg);

  const {
    tokens: amplifyTokens,
    reset: resetAmplifyToken,
    refresh: refreshAmplifyToken,
  } = amplifyTokenRefresh;
  const {
    isReady: isLiveTokenReady,
    reset: resetLiveToken,
    generate: generateLiveToken,
  } = generatedLiveToken;
  const { refetch: refetchLivePageData } = requiredLivePageData;

  const retry = React.useCallback(() => {
    if (!amplifyTokens) {
      resetAmplifyToken();
      refreshAmplifyToken();
      return;
    }

    if (!isLiveTokenReady) {
      resetLiveToken();
      generateLiveToken({});
      return;
    }

    refetchLivePageData();
  }, [
    amplifyTokens,
    resetAmplifyToken,
    refreshAmplifyToken,
    isLiveTokenReady,
    resetLiveToken,
    generateLiveToken,
    refetchLivePageData,
  ]);

  const isWaitingForLiveToken = !!amplifyTokenRefresh.tokens
    && !generatedLiveToken.isReady
    && !generatedLiveToken.isError;

  return {
    amplifyTokens: amplifyTokenRefresh.tokens,
    isReady: requiredLivePageData.isReady,
    isBootstrapping: amplifyTokenRefresh.isLoading
      || generatedLiveToken.isLoading
      || isWaitingForLiveToken
      || requiredLivePageData.isLoading,
    startupError: amplifyTokenRefresh.error
      || generatedLiveToken.error
      || requiredLivePageData.error,
    retry,
    refreshToken: refreshAmplifyToken,
  };
}
